import { Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core'
import { ControlValueAccessor, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms'
import { IonDatetime, IonicModule, IonInput, IonPopover } from '@ionic/angular'
import { format, formatISO, isValid, parse } from 'date-fns'
import equal from 'fast-deep-equal/es6'
import { DateSegment } from '../../models/DateTime/DateSegment'
import { MaskitoElementPredicate, MaskitoOptions } from '@maskito/core'
import { MaskitoDirective } from '@maskito/angular'
import { MaskitoDateMode, maskitoDateOptionsGenerator, maskitoWithPlaceholder } from '@maskito/kit'

@Component({
  selector: 'app-date-input',
  templateUrl: './date-input.component.html',
  styleUrls: ['./date-input.component.scss'],
  host: {
    '[class.disabled]': 'disabled',
  },
  imports: [FormsModule, IonicModule, MaskitoDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DateInputComponent),
      multi: true,
    },
  ],
})
export class DateInputComponent implements OnChanges, ControlValueAccessor {
  static readonly DATE_MASK: MaskitoDateMode = 'dd/mm/yyyy'
  static readonly DATE_FORMAT = 'dd. MM. yyyy'
  static readonly DATE_PLACEHOLDER = 'dd. mm. rrrr'
  static readonly DATE_SEPARATOR = '. '

  @Input() value: DateSegment | null = null
  @Input() disabled = false
  @Input() postponeChangeUntilBlur: boolean = false

  @Output() changed = new EventEmitter<DateSegment>()

  @ViewChild('dateInput') dateInput!: IonInput
  @ViewChild('datePicker') datePicker!: IonDatetime
  @ViewChild('datePopover') datePopover!: IonPopover

  protected readonly self = DateInputComponent
  protected readonly uuid = crypto.randomUUID()

  protected dateInputValue = ''
  protected datePickerValue = ''
  protected hasUnhandledChange = false
  private _latestValue: DateSegment | null = null

  private onChange: (_: any) => void = () => {}
  protected onTouched: (_: any) => void = () => {}

  protected readonly maskitoDateOptions: MaskitoOptions = DateInputComponent.getDateOptions()

  protected readonly maskitoMaskPredicate: MaskitoElementPredicate = async (el) => {
    if ('getInputElement' in el && typeof el.getInputElement === 'function') {
      return (el as unknown as HTMLIonInputElement).getInputElement()
    }

    // For testing, create a mock input element
    return document.createElement('input')
  }

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.renderValue()
      this.handleChange(changes['value'].currentValue, false, !changes['value'].isFirstChange())
    }
  }

  writeValue(value: DateSegment): void {
    this.value = value
    this.renderValue()
  }

  registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: (_: any) => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
  }

  renderValue(): void {
    if (this.value) {
      this.dateInputValue = format(this.value.toDate(), DateInputComponent.DATE_FORMAT)
      this.datePickerValue = formatISO(this.value.toDate())
    } else {
      this.dateInputValue = ''
      this.datePickerValue = ''
    }
  }

  private handleChange(newValue: DateSegment | null, canEmitChange: boolean, canFireChange: boolean): void {
    if (!equal(newValue, this._latestValue)) {
      if (canEmitChange) {
        this.changed.emit(newValue)
      }
      if (canFireChange) {
        this.onChange(newValue)
      }
    }
    this._latestValue = newValue
  }

  async onDateInputChange(): Promise<void> {
    if (this.parsedValue !== false) {
      this.value = this.parsedValue
      if (this.postponeChangeUntilBlur) {
        this.hasUnhandledChange = true
      } else {
        this.handleChange(this.value, true, true)
      }
    }
    if (this.datePopover) {
      await this.datePopover.dismiss()
    }
  }

  async onDateInputBlur($event: any): Promise<void> {
    if (this.hasUnhandledChange) {
      this.hasUnhandledChange = false
      this.handleChange(this.value, true, true)
    }
    this.onTouched($event)
  }

  onDatePickerChange(): void {
    if (this.datePickerValue) {
      this.value = new DateSegment(new Date(this.datePickerValue))
      this.renderValue()
      this.handleChange(this.value, true, true)
    }
  }

  protected async onDateIconClick($event: Event): Promise<void> {
    this.datePickerValue = this.parsedValue ? formatISO(this.parsedValue.toDate()) : ''
    this.onTouched($event)
    await this.datePopover?.present()
  }

  /**
   * Returns a DateSegment object if the input is valid, NULL when it's empty, or FALSE if it's invalid.
   * @returns {DateSegment}
   */
  get parsedValue(): DateSegment | null | false {
    const inputStr = this.dateInput.value.toString()
    if (inputStr.trim() === '') {
      return null
    }
    const date = parse(inputStr, DateInputComponent.DATE_FORMAT, new Date())
    return isValid(date) ? new DateSegment(date) : false
  }

  private static getDateOptions(): MaskitoOptions {
    const { plugins, ...placeholderOptions } = maskitoWithPlaceholder(DateInputComponent.DATE_PLACEHOLDER, false)

    const options = maskitoDateOptionsGenerator({
      mode: DateInputComponent.DATE_MASK,
      separator: DateInputComponent.DATE_SEPARATOR,
    })

    return {
      ...options,
      plugins: plugins.concat(options.plugins || []),
      preprocessors: [...placeholderOptions.preprocessors, ...options.preprocessors],
      postprocessors: [...options.postprocessors, ...placeholderOptions.postprocessors],
    }
  }
}
